import { ADMIN_NOTIFICATION_SOP_FLOW_TYPES } from '@/lib/admin-notification-flow-issues';
import { categoryLabel, reactActionUrl, type AdminNotification as ApiAdminNotification } from '@/lib/notifications';
import type { AdminNotificationStackItem, AdminNotificationSeverity } from '@/lib/admin-notification-stack.types';

function detailsLookLikeSopFlowPaths(details: string): boolean {
  return (
    details.includes('Cycle:') ||
    details.includes('→') ||
    details.includes('flow cycle (')
  );
}

function mapSeverity(apiSeverity: string): AdminNotificationSeverity {
  if (apiSeverity === 'error') return 'critical';
  if (apiSeverity === 'warning') return 'warning';
  return 'info';
}

/** Strip delivery-rule applies-to suffixes like `(site: X · scenario: Y)` — rule name usually already encodes scope. */
export function compactAdminNotificationMessage(message: string): string {
  return message
    .replace(/\s*\((?:site|dealer|scenario)s?:\s*[^)]+\)/gi, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

function normalizeForCompare(value: string): string {
  return value.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
}

function containsMostTokens(haystack: string, needle: string): boolean {
  const hay = normalizeForCompare(haystack);
  const tokens = normalizeForCompare(needle)
    .split(/\s+/)
    .filter((token) => token.length > 2);
  if (!tokens.length) return false;
  const matched = tokens.filter((token) => hay.includes(token)).length;
  return matched / tokens.length >= 0.7;
}

/**
 * Pick a single secondary line for header/stack cards.
 * Prefer actionable `details`; skip `context` when it only restates the rule/site/scenario already in the message.
 */
export function compactAdminNotificationSubtitle(input: {
  message: string;
  context: string;
  details: string;
  suppressPathDetails?: boolean;
}): string | undefined {
  const message = input.message.trim();
  const context = input.context.trim();
  const details = input.details.trim();
  const suppressPathDetails = input.suppressPathDetails === true;

  const usableDetails =
    details && !suppressPathDetails && details !== message ? details : '';

  if (usableDetails) return usableDetails;

  if (!context || context === message) return undefined;

  // Keep membership / path hops that add a concrete target not already in the message.
  if (context.includes('→')) {
    const hops = context
      .split('→')
      .map((part) => part.trim())
      .filter(Boolean);
    const uniqueHops = hops.filter((hop) => !containsMostTokens(message, hop));
    if (uniqueHops.length) return uniqueHops.join(' → ');
    return undefined;
  }

  // Labeled restatements ("Delivery Rule: … · Site: … · Scenario: …") are redundant in the quick stack.
  if (/^(delivery rule|site|dealer|sop|scenario)\b/i.test(context) && containsMostTokens(message, context)) {
    return undefined;
  }

  if (containsMostTokens(message, context)) return undefined;
  return context;
}

/** Map first-class API admin_notifications rows to the stack UI model. */
export function mapApiAdminNotificationToStackItem(row: ApiAdminNotification): AdminNotificationStackItem {
  const href = reactActionUrl(row.actionUrl);
  const context = row.context?.trim() || '';
  const details = row.details?.trim() || '';
  const suppressPathDetails = ADMIN_NOTIFICATION_SOP_FLOW_TYPES.has(row.type) && detailsLookLikeSopFlowPaths(details);
  const compactMessage = compactAdminNotificationMessage(row.message);
  const subtitle = compactAdminNotificationSubtitle({
    message: compactMessage,
    context,
    details,
    suppressPathDetails,
  });
  const canAct = row.canAct === true;
  const actionHref = canAct ? (href ?? undefined) : undefined;
  const linkKind = row.metadata?.link_kind;
  const actionLabel = actionHref
    ? linkKind === 'investigate'
      ? 'Review'
      : 'Fix'
    : undefined;
  return {
    id: row.key,
    dedupeKey: row.key,
    category: row.category,
    severity: mapSeverity(row.severity),
    title: categoryLabel(row.category),
    message: compactMessage,
    subtitle,
    createdAt: new Date(row.timestamp).toISOString(),
    actionLabel,
    actionHref,
    status: 'active',
    sopId: row.sopId,
    siteId: row.siteId,
    entityType: row.entityType,
    entityId: row.entityId,
  };
}

export function mapApiAdminNotificationsToStackItems(
  rows: ApiAdminNotification[],
): AdminNotificationStackItem[] {
  return rows.map(mapApiAdminNotificationToStackItem);
}
